import React, { useRef, useState, useEffect, useLayoutEffect, useCallback, useMemo } from "react";
import { useFrame } from "@/contexts/FrameContext";
import { useCoreApp } from "@/contexts/CoreAppContext";
import { useComputedValues } from "@/contexts/ComputedValuesContext";
import { useZoom } from "@/contexts/ZoomContext";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { generateAnnotationThumbnail } from "@/utils/canvasUtils";
import { createWatermark, applyWatermarkToCanvas } from "@/utils/watermark";
import { v4 as uuidv4 } from "uuid";
import { getScaledRect, getScaleFactors, scaleRect, Point, Rect } from "@utils/scaleUtils";
import {
    AnnotationEditRequestDetails,
    AnnotationStrategy,
    DrawingState,
    MinimalAnnotation,
    ResizeHandle
} from "../../utils/annotationStrategies/types";
import { BoundingBox } from "../../utils/annotationStrategies/BoundingBox";
import { getLabelColor } from "@/components/annotation/colors";
import { clearMaskCache } from "@/utils/maskRenderer";

import { DEFAULT_LABEL } from "./colors";
import { useFlow } from "@/contexts/FlowContext";
import { useFlowState } from "@/hooks/useFlowState";
import { useModel } from "@/contexts/ModelContext";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import { AnalyticsFlowEvent, AppSource } from "@/analytics/types";
import { useCurrentMedia, useMarkLabelAsEdited } from "@/store";
import { FileInputDimensions } from "@/types/media";
import { Annotation } from "@/types/annotation";
import { LABEL_TOP_PADDING } from "./colors";
import { useKeyboardShortcut } from "@/hooks/useKeyboardShortcut";

// --- Component Props ---
export interface AnnotationCanvasProps<T extends MinimalAnnotation> {
    strategy: AnnotationStrategy<T>;
    mediaRef: React.RefObject<HTMLImageElement | HTMLVideoElement>;
    containerRef: React.RefObject<HTMLDivElement>; // To fit canvas to image within container
    className?: string;
    // Callback for when image dimensions are known and canvas is sized
    onCanvasReady?: (dimensions: FileInputDimensions) => void;
    forceHideLabels?: boolean;
    editingAnnotationId?: string | null; // ID of annotation being edited by parent
    onAnnotationEditRequest?: (details: AnnotationEditRequestDetails<T>) => void; // Event emitter
    onCanvasClickWithoutAnnotation?: () => void; // To close input when clicking empty canvas space
    selectedAnnotationIdFromContext?: string | null; // New prop for external selection
    serverMediaDimensions?: FileInputDimensions;
    // Optional frame image for accurate thumbnail generation (used for videos)
    currentFrameImageUrl?: string | null;
    // Force canvas realignment (useful when parent detects layout changes)
    forceRealign?: number; // Increment this to trigger realignment
    // Zoom props for coordinate transformation
    zoomLevel?: number;
    zoomX?: number;
    zoomY?: number;
    // Watermark options
    includeWatermark?: boolean;
    watermarkQrUrl?: string;
    // Callback for when hover state changes - provides the hovered annotation's bounds and optional SVG path
    onHoverChange?: (
        hoveredBounds: {
            x: number;
            y: number;
            width: number;
            height: number;
            svgPath?: string;
        } | null
    ) => void;
    // Optional: Direct annotations to render (bypasses context, makes canvas read-only)
    // When provided, the canvas will only render these annotations without any interaction
    externalAnnotations?: T[];
    // Optional: Source dimensions for external annotations (used for scaling)
    // When externalAnnotations is provided, this specifies the original image dimensions
    // the annotations are defined in (e.g., { width: 640, height: 480 })
    externalSourceDimensions?: { width: number; height: number };
}
export const AnnotationCanvas = <T extends MinimalAnnotation>(props: AnnotationCanvasProps<T>) => {
    const {
        strategy,
        mediaRef,
        containerRef,
        className,
        onCanvasReady,
        forceHideLabels,
        editingAnnotationId,
        onAnnotationEditRequest,
        onCanvasClickWithoutAnnotation,
        selectedAnnotationIdFromContext,
        serverMediaDimensions,
        currentFrameImageUrl,
        forceRealign,
        zoomLevel = 1,
        zoomX = 0,
        zoomY = 0,
        includeWatermark = false,
        watermarkQrUrl = "https://rapid.roboflow.com",
        onHoverChange,
        externalAnnotations,
        externalSourceDimensions
    } = props;
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [drawingState, setDrawingState] = useState<DrawingState<T>>({ action: "idle" });
    const [selectedAnnotation, setSelectedAnnotation] = useState<T | null>(null);
    const [previewAnnotation, setPreviewAnnotation] = useState<T | null>(null); // For live drawing/resizing
    // Store actual rendered image dimensions and offset for accurate coordinate mapping
    const [renderedImageGeom, setRenderedImageGeom] = useState<{
        width: number;
        height: number;
        offsetX: number;
        offsetY: number;
    } | null>(null);
    // Ref to track previous geometry values to avoid unnecessary state updates during zoom/pan
    const prevRenderedImageGeomRef = useRef<{
        width: number;
        height: number;
        offsetX: number;
        offsetY: number;
    } | null>(null);
    const [mousePosition, setMousePosition] = useState<Point | null>(null); // For guide lines
    const [guideLineColor, setGuideLineColor] = useState<string>("rgba(255, 255, 255, 0.7)"); // Adaptive guide line color
    const [labelHoveredAnnotationId, setLabelHoveredAnnotationId] = useState<string | null>(null); // Track when hovering specifically over a label
    const [isMiddleMousePanning, setIsMiddleMousePanning] = useState(false);
    const [middleMousePanStart, setMiddleMousePanStart] = useState<Point | null>(null);
    const thumbnailGenerationRef = useRef<boolean>(false);
    const [originalMediaDimensions, setOriginalMediaDimensions] = useState<{
        width: number;
        height: number;
    } | null>(null);
    const [watermarkCanvas, setWatermarkCanvas] = useState<HTMLCanvasElement | null>(null);

    // Use optimized hooks for better performance
    const { promptLabels } = useModel();
    const promptLabelsArray = useMemo(() => Array.from(promptLabels.values()), [promptLabels]);
    const {
        inferredDimensions: computedInferredDimensions,
        currentFrameAnnotations,
        currentFramePredictions
    } = useComputedValues();
    const inferredDimensions = useMemo(() => {
        return computedInferredDimensions || originalMediaDimensions;
    }, [computedInferredDimensions, originalMediaDimensions]);
    const {
        setSelectedAnnotationId,
        setShouldShowAnnotationInput,
        hoveredAnnotationId: contextHoveredAnnotationId,
        setHoveredAnnotationId: contextSetHoveredAnnotationId
    } = useFrame();

    // Use local hover state for external annotations mode to avoid cross-canvas interference
    const [localHoveredAnnotationId, setLocalHoveredAnnotationId] = useState<string | null>(null);
    const hoveredAnnotationId = externalAnnotations
        ? localHoveredAnnotationId
        : contextHoveredAnnotationId;
    const setHoveredAnnotationId = externalAnnotations
        ? setLocalHoveredAnnotationId
        : contextSetHoveredAnnotationId;
    const { selectedObject, previouslySelectedObject, setPreviouslySelectedObject } = useCoreApp();
    const labelToUse = useMemo(
        () =>
            selectedObject ??
            previouslySelectedObject ??
            promptLabelsArray?.[0]?.name ??
            DEFAULT_LABEL,
        [selectedObject, previouslySelectedObject, promptLabelsArray]
    );
    const { pan, setZoom, zoomLevel: contextZoomLevel } = useZoom();
    const { setCurrentFrameAnnotations, setCurrentFramePredictions, flowLocked } = useFlow();
    const { onlyShowPredictions } = useFlowState();
    const { track } = useAnalytics();
    const currentMedia = useCurrentMedia();
    const markLabelAsEdited = useMarkLabelAsEdited();

    // Memoize predictions array to prevent unnecessary recalculations
    // Use a ref to track the previous array for shallow comparison
    const prevPredictionsRef = useRef(currentFramePredictions);
    const predictionsChanged =
        prevPredictionsRef.current !== currentFramePredictions ||
        prevPredictionsRef.current.length !== currentFramePredictions.length;

    if (predictionsChanged) {
        prevPredictionsRef.current = currentFramePredictions;
    }

    const visibleCurrentFramePredictions = useMemo(() => {
        return currentFramePredictions.filter((p) => {
            const promptLabel = promptLabels.get(p.class);
            if (!promptLabel) return false;
            return p.confidence >= promptLabel.confidenceThreshold;
        });
    }, [currentFramePredictions, promptLabels, predictionsChanged]);

    // Memoize scale factors separately to avoid recalculating
    const scaleFactors = useMemo(() => {
        if (renderedImageGeom && inferredDimensions) {
            const factors = getScaleFactors({
                source: inferredDimensions,
                target: renderedImageGeom
            });
            return {
                ...factors,
                needScale:
                    Math.abs(factors.scaleX - 1) > 0.0001 || Math.abs(factors.scaleY - 1) > 0.0001
            };
        }
        return null;
    }, [renderedImageGeom, inferredDimensions]);

    // Build a color map for all labels once - valid across all frames and files
    // This prevents recalculating colors on every frame change
    // Uses promptLabelsArray to detect changes to label properties (like customColor)
    const labelColorMap = useMemo(() => {
        const map = new Map<string, string>();
        promptLabels.forEach((label, name) => {
            map.set(name, getLabelColor(promptLabels, name));
        });
        return map;
    }, [promptLabels, promptLabelsArray]);

    // Track previous visibleCurrentFramePredictions to avoid recalculating when array reference changes but content is same
    const prevVisiblePredictionsRef = useRef(visibleCurrentFramePredictions);
    const visiblePredictionsChanged =
        prevVisiblePredictionsRef.current !== visibleCurrentFramePredictions ||
        prevVisiblePredictionsRef.current.length !== visibleCurrentFramePredictions.length;

    if (visiblePredictionsChanged) {
        prevVisiblePredictionsRef.current = visibleCurrentFramePredictions;
    }

    const visibleCurrentFramePredictionsScaled = useMemo(() => {
        if (!scaleFactors || !currentMedia?.file.id) return [];

        const { scaleX, scaleY, needScale } = scaleFactors;
        const fileId = currentMedia.file.id;
        const frameId = currentMedia.frame?.id;

        return visibleCurrentFramePredictions.map((p) => {
            return {
                ...p,
                ...(needScale ? getScaledRect({ rect: p, scaleX, scaleY }) : {}),
                color: labelColorMap.get(p.class)!,
                label: `${(p as any).class}`,
                id: p.detection_id,
                fileId,
                frameId
            } as Annotation;
        });
    }, [
        scaleFactors,
        visibleCurrentFramePredictions,
        visiblePredictionsChanged,
        labelColorMap,
        currentMedia?.file.id,
        currentMedia?.frame?.id
    ]);

    const visibleCurrentFrameAnnotations = useMemo(() => {
        // Text prompting mode: filter annotations by confidence threshold
        // But if the label has user edits (hasDiffs), show all annotations for that label
        return currentFrameAnnotations.filter((anno) => {
            const promptLabel = promptLabels.get(anno.label);
            if (!promptLabel) return false;
            // If user has edited this label, don't filter by confidence
            if (promptLabel.hasDiffs) return true;
            return !anno.confidence || anno.confidence >= promptLabel.confidenceThreshold;
        });
    }, [currentFrameAnnotations, promptLabels]);

    // Memoize annotation scale factors separately
    const annotationScaleFactors = useMemo(() => {
        if (renderedImageGeom && originalMediaDimensions) {
            return getScaleFactors({ source: originalMediaDimensions, target: renderedImageGeom });
        }
        return null;
    }, [renderedImageGeom, originalMediaDimensions]);

    const currentFrameAnnotationsScaled = useMemo(() => {
        if (onlyShowPredictions || !annotationScaleFactors) return [];

        const { scaleX, scaleY } = annotationScaleFactors;
        // Calculate needScale once instead of in every iteration
        const needScale = Math.abs(scaleX - 1) > 0.0001 || Math.abs(scaleY - 1) > 0.0001;

        return (visibleCurrentFrameAnnotations as unknown as BoundingBox[]).map((annotation) => {
            return {
                ...annotation,
                ...(needScale ? getScaledRect({ rect: annotation, scaleX, scaleY }) : {}),
                color: labelColorMap.get(annotation.label)!
            } as Annotation;
        });
    }, [
        visibleCurrentFrameAnnotations,
        annotationScaleFactors,
        onlyShowPredictions,
        labelColorMap
    ]);

    const isViewOnly = useMemo(() => {
        // View-only when flow is locked, only showing predictions, or external annotations provided
        if (flowLocked || onlyShowPredictions || externalAnnotations) return true;
        return false;
    }, [flowLocked, onlyShowPredictions, externalAnnotations]);

    // Calculate scale factors specifically for external annotations
    const externalScaleFactors = useMemo(() => {
        if (externalAnnotations && externalSourceDimensions && renderedImageGeom) {
            return getScaleFactors({ source: externalSourceDimensions, target: renderedImageGeom });
        }
        return null;
    }, [externalAnnotations, externalSourceDimensions, renderedImageGeom]);

    const annotationsToDrawScaled = useMemo(() => {
        // If external annotations provided, use them directly (read-only mode)
        if (externalAnnotations && externalAnnotations.length > 0) {
            // Use external scale factors if available, otherwise fall back to context scale factors
            const effectiveScaleFactors = externalScaleFactors || scaleFactors;
            if (!effectiveScaleFactors) return [];
            const scaled = externalAnnotations.map((anno) => ({
                ...anno,
                x: anno.x * effectiveScaleFactors.scaleX,
                y: anno.y * effectiveScaleFactors.scaleY,
                width: anno.width * effectiveScaleFactors.scaleX,
                height: anno.height * effectiveScaleFactors.scaleY
            }));
            // Sort by area (smallest first) so larger boxes are drawn on top
            scaled.sort((a, b) => a.width * a.height - b.width * b.height);
            return scaled;
        }

        // In review/preview/deploy mode, show predictions instead of annotations
        // This allows users to see what the model predicted without editing
        let annotations: Annotation[];

        if (onlyShowPredictions) {
            // Use predictions when in review/preview/deploy mode
            annotations = [...visibleCurrentFramePredictionsScaled];
        } else {
            // Use annotations when in prompting mode (the default editing flow)
            annotations = [...currentFrameAnnotationsScaled.filter((a) => !a.draft)];
        }

        // Sort by area (smallest first) so larger boxes are drawn on top
        annotations.sort((a, b) => {
            const areaA = a.width * a.height;
            const areaB = b.width * b.height;
            return areaA - areaB; // Ascending order (smallest to largest)
        });

        return annotations;
    }, [
        currentFrameAnnotationsScaled,
        visibleCurrentFramePredictionsScaled,
        onlyShowPredictions,
        externalAnnotations,
        scaleFactors,
        externalScaleFactors
    ]);

    // Memoize onHoverChange to avoid effect re-runs when parent re-renders
    const onHoverChangeStable = useCallback(
        (
            bounds: { x: number; y: number; width: number; height: number; svgPath?: string } | null
        ) => {
            onHoverChange?.(bounds);
        },
        [onHoverChange]
    );

    // Notify parent when hover state changes
    useEffect(() => {
        if (hoveredAnnotationId && annotationsToDrawScaled && renderedImageGeom) {
            const annotation = (annotationsToDrawScaled as unknown as ReadonlyArray<T>).find(
                (a) => a.id === hoveredAnnotationId
            );
            if (annotation) {
                const bbox = annotation as unknown as BoundingBox;
                // Use getBoundsWithLabel if available to include label area in the cutout
                // This prevents the label from being dimmed when hovering the box
                let bounds: { x: number; y: number; width: number; height: number };
                if (strategy.getBoundsWithLabel) {
                    bounds = strategy.getBoundsWithLabel(annotation);
                } else {
                    bounds = {
                        x: bbox.x - bbox.width / 2,
                        y: bbox.y - bbox.height / 2,
                        width: bbox.width,
                        height: bbox.height
                    };
                }
                // Get SVG path for precise cutout if available
                const svgPath = strategy.getSvgCutoutPath?.(annotation, zoomLevel);
                // Scale the bounds by zoomLevel since the parent uses screen coordinates
                onHoverChangeStable({
                    x: bounds.x * zoomLevel,
                    y: bounds.y * zoomLevel,
                    width: bounds.width * zoomLevel,
                    height: bounds.height * zoomLevel,
                    svgPath
                });
            }
        } else {
            onHoverChangeStable(null);
        }
    }, [
        hoveredAnnotationId,
        annotationsToDrawScaled,
        onHoverChangeStable,
        renderedImageGeom,
        zoomLevel,
        strategy
    ]);

    const [cursor, setCursor] = useState<string>(
        isViewOnly || onlyShowPredictions ? "not-allowed" : "crosshair"
    );

    // --- Helper Methods for Moving and Resizing Annotations ---
    const moveAnnotation = useCallback(
        (annotation: T, deltaX: number, deltaY: number): T | null => {
            if (!renderedImageGeom) return null;

            const boundingBoxAnnotation = annotation as unknown as BoundingBox;
            const newX = Math.max(
                boundingBoxAnnotation.width / 2,
                Math.min(
                    renderedImageGeom.width - boundingBoxAnnotation.width / 2,
                    boundingBoxAnnotation.x + deltaX
                )
            );
            const newY = Math.max(
                boundingBoxAnnotation.height / 2,
                Math.min(
                    renderedImageGeom.height - boundingBoxAnnotation.height / 2,
                    boundingBoxAnnotation.y + deltaY
                )
            );

            return {
                ...annotation,
                x: newX,
                y: newY,
                draft: false // Remove draft state when annotation is moved
            } as unknown as T;
        },
        [renderedImageGeom]
    );

    // Helper to find annotation at point
    const findAnnotationAtPoint = useCallback(
        (point: Point): T | null => {
            for (const anno of annotationsToDrawScaled as unknown as ReadonlyArray<T>) {
                if (strategy.isPointInAnnotation(point, anno)) {
                    return anno;
                }
            }
            return null;
        },
        [annotationsToDrawScaled, strategy]
    );

    // Helper to handle annotation selection
    const handleAnnotationSelection = useCallback(
        (annotation: T | null) => {
            if (annotation) {
                setSelectedAnnotation(annotation);
                setSelectedAnnotationId(annotation.id);
            } else {
                setSelectedAnnotation(null);
                setSelectedAnnotationId(null);
            }
        },
        [setSelectedAnnotationId]
    );

    // Helper to clear selection and trigger empty space callback
    const handleEmptySpaceClick = useCallback(() => {
        // If there's a selected annotation being deselected, mark its label as edited
        // This handles the case where user draws a box with a pre-selected label
        // and clicks away to confirm without using the AnnotationLabelInput
        // Only mark as edited if the annotation is user-drawn (no confidence score)
        // Draft annotations from predictions should NOT trigger hasDiffs
        if (selectedAnnotation?.label && !selectedAnnotation.confidence) {
            markLabelAsEdited(selectedAnnotation.label);
        }
        handleAnnotationSelection(null);
        if (onCanvasClickWithoutAnnotation) {
            onCanvasClickWithoutAnnotation();
        }
    }, [
        handleAnnotationSelection,
        onCanvasClickWithoutAnnotation,
        selectedAnnotation,
        markLabelAsEdited
    ]);

    // Helper to update hover state
    const updateHoverState = useCallback(
        (annotation: T | null, cursor: string) => {
            setHoveredAnnotationId(annotation?.id || null);
            setCursor(cursor);
        },
        [setCursor, setHoveredAnnotationId]
    );

    const resizeAnnotation = useCallback(
        (
            annotation: T,
            handle: ResizeHandle,
            deltaX: number,
            deltaY: number,
            originalAnnotation: T
        ): T | null => {
            if (!renderedImageGeom) return null;

            const boundingBoxAnnotation = annotation as unknown as BoundingBox;
            const originalBoundingBox = originalAnnotation as unknown as BoundingBox;

            let newX = boundingBoxAnnotation.x;
            let newY = boundingBoxAnnotation.y;
            let newWidth = boundingBoxAnnotation.width;
            let newHeight = boundingBoxAnnotation.height;

            // Apply resize deltas based on handle
            switch (handle) {
                case "nw":
                    newWidth = Math.max(10, originalBoundingBox.width - deltaX);
                    newHeight = Math.max(10, originalBoundingBox.height - deltaY);
                    newX = originalBoundingBox.x + (originalBoundingBox.width - newWidth) / 2;
                    newY = originalBoundingBox.y + (originalBoundingBox.height - newHeight) / 2;
                    break;
                case "ne":
                    newWidth = Math.max(10, originalBoundingBox.width + deltaX);
                    newHeight = Math.max(10, originalBoundingBox.height - deltaY);
                    newX = originalBoundingBox.x + (newWidth - originalBoundingBox.width) / 2;
                    newY = originalBoundingBox.y + (originalBoundingBox.height - newHeight) / 2;
                    break;
                case "sw":
                    newWidth = Math.max(10, originalBoundingBox.width - deltaX);
                    newHeight = Math.max(10, originalBoundingBox.height + deltaY);
                    newX = originalBoundingBox.x + (originalBoundingBox.width - newWidth) / 2;
                    newY = originalBoundingBox.y + (newHeight - originalBoundingBox.height) / 2;
                    break;
                case "se":
                    newWidth = Math.max(10, originalBoundingBox.width + deltaX);
                    newHeight = Math.max(10, originalBoundingBox.height + deltaY);
                    newX = originalBoundingBox.x + (newWidth - originalBoundingBox.width) / 2;
                    newY = originalBoundingBox.y + (newHeight - originalBoundingBox.height) / 2;
                    break;
                case "n":
                    newHeight = Math.max(10, originalBoundingBox.height - deltaY);
                    newY = originalBoundingBox.y + (originalBoundingBox.height - newHeight) / 2;
                    break;
                case "s":
                    newHeight = Math.max(10, originalBoundingBox.height + deltaY);
                    newY = originalBoundingBox.y + (newHeight - originalBoundingBox.height) / 2;
                    break;
                case "w":
                    newWidth = Math.max(10, originalBoundingBox.width - deltaX);
                    newX = originalBoundingBox.x + (originalBoundingBox.width - newWidth) / 2;
                    break;
                case "e":
                    newWidth = Math.max(10, originalBoundingBox.width + deltaX);
                    newX = originalBoundingBox.x + (newWidth - originalBoundingBox.width) / 2;
                    break;
            }

            // Clamp to canvas bounds
            const minX = newWidth / 2;
            const maxX = renderedImageGeom.width - newWidth / 2;
            const minY = newHeight / 2;
            const maxY = renderedImageGeom.height - newHeight / 2;

            newX = Math.max(minX, Math.min(maxX, newX));
            newY = Math.max(minY, Math.min(maxY, newY));

            return {
                ...annotation,
                x: newX,
                y: newY,
                width: newWidth,
                height: newHeight,
                draft: false // Remove draft state when annotation is resized
            } as unknown as T;
        },
        [renderedImageGeom]
    );

    // Effect to handle external selection changes - only process when canvas is editable
    useEffect(() => {
        // Skip selection processing in read-only mode for performance
        if (isViewOnly) {
            return;
        }

        const currentAnnotations = annotationsToDrawScaled as unknown as T[];
        if (selectedAnnotationIdFromContext) {
            const annotationToSelect = currentAnnotations.find(
                (anno) => anno.id === selectedAnnotationIdFromContext
            );
            setSelectedAnnotation((currentSelectedAnno) => {
                if (annotationToSelect) {
                    if (currentSelectedAnno?.id !== annotationToSelect.id) {
                        return annotationToSelect;
                    }
                    return currentSelectedAnno;
                } else {
                    if (currentSelectedAnno?.id === selectedAnnotationIdFromContext) {
                        return null;
                    }
                    return currentSelectedAnno;
                }
            });
        } else {
            // If selectedAnnotationIdFromContext is null, clear the internal selection
            setSelectedAnnotation(null);
        }
    }, [
        isViewOnly,
        selectedAnnotationIdFromContext,
        currentFrameAnnotations,
        annotationsToDrawScaled
    ]);

    // Helper function to generate thumbnails using the current frame image for videos
    const generateThumbnailForCurrentFrame = useCallback(
        async (annotationBox: Rect): Promise<string | null> => {
            const mediaElement = mediaRef.current;
            if (!mediaElement) return null;

            // For videos, use the frame image URL if available; otherwise use the video element
            if (mediaElement instanceof HTMLVideoElement && currentFrameImageUrl) {
                // Create an image element from the frame URL
                return new Promise((resolve) => {
                    const frameImg = new Image();
                    frameImg.onload = async () => {
                        try {
                            const result = await generateAnnotationThumbnail({
                                mediaElement: frameImg,
                                unscaledAnnotationBox: annotationBox
                            });
                            resolve(result);
                        } catch (error) {
                            // Fallback to video element
                            const fallbackResult = await generateAnnotationThumbnail({
                                mediaElement,
                                unscaledAnnotationBox: annotationBox
                            });
                            resolve(fallbackResult);
                        }
                    };
                    frameImg.onerror = async () => {
                        const fallbackResult = await generateAnnotationThumbnail({
                            mediaElement,
                            unscaledAnnotationBox: annotationBox
                        });
                        resolve(fallbackResult);
                    };
                    frameImg.src = currentFrameImageUrl;
                });
            } else {
                // For images or when no frame URL is available, use the media element directly
                const thumbnail = await generateAnnotationThumbnail({
                    mediaElement,
                    unscaledAnnotationBox: annotationBox
                });
                return thumbnail;
            }
        },
        [mediaRef, currentFrameImageUrl]
    );

    // Analyze image brightness to determine optimal guide line color
    const analyzeImageBrightness = useCallback(
        (mediaElement: HTMLImageElement | HTMLVideoElement) => {
            try {
                // Create a small canvas to sample the image
                const sampleCanvas = document.createElement("canvas");
                const sampleSize = 50; // Sample a 50x50 area for performance
                sampleCanvas.width = sampleSize;
                sampleCanvas.height = sampleSize;
                const ctx = sampleCanvas.getContext("2d");
                if (!ctx) return;

                // Draw the media element scaled down to sample size
                if (mediaElement instanceof HTMLImageElement) {
                    ctx.drawImage(mediaElement, 0, 0, sampleSize, sampleSize);
                } else if (mediaElement instanceof HTMLVideoElement) {
                    ctx.drawImage(mediaElement, 0, 0, sampleSize, sampleSize);
                }

                // Get image data and calculate average brightness
                const imageData = ctx.getImageData(0, 0, sampleSize, sampleSize);
                const data = imageData.data;
                let totalBrightness = 0;
                const pixelCount = data.length / 4;

                for (let i = 0; i < data.length; i += 4) {
                    // Calculate perceived brightness using luminance formula
                    const r = data[i];
                    const g = data[i + 1];
                    const b = data[i + 2];
                    const brightness = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
                    totalBrightness += brightness;
                }

                const avgBrightness = totalBrightness / pixelCount;

                // If image is predominantly bright (>70% brightness), use dark guidelines
                if (avgBrightness > 0.7) {
                    setGuideLineColor("rgba(0, 0, 0, 0.7)");
                } else {
                    setGuideLineColor("rgba(255, 255, 255, 0.7)");
                }
            } catch {
                // If analysis fails (e.g., CORS), default to white
                setGuideLineColor("rgba(255, 255, 255, 0.7)");
            }
        },
        []
    );

    // Update guide line color when media changes
    useEffect(() => {
        const mediaElement = mediaRef.current;
        if (!mediaElement) return;

        const handleLoad = () => {
            analyzeImageBrightness(mediaElement);
        };

        if (mediaElement instanceof HTMLImageElement) {
            if (mediaElement.complete && mediaElement.naturalWidth > 0) {
                analyzeImageBrightness(mediaElement);
            } else {
                mediaElement.addEventListener("load", handleLoad);
                return () => mediaElement.removeEventListener("load", handleLoad);
            }
        } else if (mediaElement instanceof HTMLVideoElement) {
            if (mediaElement.readyState >= 1) {
                analyzeImageBrightness(mediaElement);
            } else {
                mediaElement.addEventListener("loadeddata", handleLoad);
                return () => mediaElement.removeEventListener("loadeddata", handleLoad);
            }
        }
    }, [mediaRef, analyzeImageBrightness, currentMedia?.file.id, currentMedia?.frame?.id]);

    // --- Canvas Setup & Resizing ---
    const setupCanvas = useCallback(() => {
        requestAnimationFrame(() => {
            const canvas = canvasRef.current;
            const mediaElement = mediaRef.current;
            const container = containerRef.current;

            if (!canvas || !mediaElement || !container) {
                return;
            }

            let mediaHasDimensions = false;
            let mediaNaturalW = 0,
                mediaNaturalH = 0;

            // Prefer server dimensions if available
            if (serverMediaDimensions) {
                mediaHasDimensions = true;
                mediaNaturalW = serverMediaDimensions.width;
                mediaNaturalH = serverMediaDimensions.height;
            } else {
                // Fallback to client-side dimensions
                if (mediaElement instanceof HTMLImageElement) {
                    mediaHasDimensions = mediaElement.complete && mediaElement.naturalWidth > 0;
                    mediaNaturalW = mediaElement.naturalWidth;
                    mediaNaturalH = mediaElement.naturalHeight;
                } else if (mediaElement instanceof HTMLVideoElement) {
                    mediaHasDimensions =
                        mediaElement.readyState >= 1 && mediaElement.videoWidth > 0;
                    mediaNaturalW = mediaElement.videoWidth;
                    mediaNaturalH = mediaElement.videoHeight;
                }
            }

            if (!mediaHasDimensions) {
                return;
            }

            // Use clientWidth/clientHeight for base dimensions (un-transformed)
            // getBoundingClientRect includes CSS transforms, which causes flickering during zoom
            const baseWidth = mediaElement.clientWidth;
            const baseHeight = mediaElement.clientHeight;

            if (baseWidth === 0 || baseHeight === 0) {
                return;
            }

            // Store original media dimensions
            setOriginalMediaDimensions({ width: mediaNaturalW, height: mediaNaturalH });

            // Skip label padding for external annotations mode to keep positioning simple
            const effectiveLabelPadding = externalAnnotations ? 0 : LABEL_TOP_PADDING;

            const canvasStyleWidth = baseWidth;
            const canvasStyleHeight = baseHeight + effectiveLabelPadding;

            // Only update state if geometry actually changed to avoid flickering during zoom/pan
            const newGeom = { width: canvasStyleWidth, height: baseHeight, offsetX: 0, offsetY: 0 };

            const prevGeom = prevRenderedImageGeomRef.current;
            const hasChanged =
                !prevGeom ||
                Math.abs(prevGeom.width - newGeom.width) > 0.5 ||
                Math.abs(prevGeom.height - newGeom.height) > 0.5;

            canvas.style.position = "absolute";
            canvas.style.left = "50%";
            canvas.style.top = "50%";
            // Only apply marginTop adjustment when there's label padding to compensate for
            canvas.style.marginTop =
                effectiveLabelPadding > 0 ? `-${(effectiveLabelPadding * zoomLevel) / 2}px` : "0px";
            canvas.style.width = `${canvasStyleWidth}px`;
            canvas.style.height = `${canvasStyleHeight}px`;
            canvas.dataset.labelPadding = effectiveLabelPadding.toString();
            canvas.dataset.sidePadding = "0";

            if (!hasChanged) {
                return;
            }

            const dpr = window.devicePixelRatio || 1;

            const targetCanvasWidth = Math.round(canvasStyleWidth * dpr);
            const targetCanvasHeight = Math.round(canvasStyleHeight * dpr);

            canvas.width = targetCanvasWidth;
            canvas.height = targetCanvasHeight;

            const ctx = canvas.getContext("2d");
            if (ctx) {
                ctx.scale(dpr, dpr);
                ctx.translate(0, effectiveLabelPadding);
            }

            prevRenderedImageGeomRef.current = newGeom;
            setRenderedImageGeom(newGeom);

            if (onCanvasReady) {
                onCanvasReady({ width: canvasStyleWidth, height: baseHeight });
            }
        });
    }, [
        mediaRef,
        containerRef,
        onCanvasReady,
        serverMediaDimensions,
        zoomLevel,
        externalAnnotations
    ]);

    useEffect(() => {
        const mediaElement = mediaRef.current;
        const containerElement = containerRef.current;

        if (!mediaElement || !containerElement) {
            return;
        }

        const handleMediaLoadOrResize = () => {
            setupCanvas();
        };

        let eventName = "load";
        let isMediaReady = false;

        if (mediaElement instanceof HTMLImageElement) {
            eventName = "load";
            isMediaReady = mediaElement.complete && mediaElement.naturalWidth > 0;
        } else if (mediaElement instanceof HTMLVideoElement) {
            eventName = "loadedmetadata";
            isMediaReady = mediaElement.readyState >= 1 && mediaElement.videoWidth > 0;
        }

        if (isMediaReady) {
            requestAnimationFrame(() => {
                if (mediaRef.current) {
                    setupCanvas();
                }
            });
        } else {
            mediaElement.addEventListener(eventName, handleMediaLoadOrResize);
        }

        // Set up ResizeObserver to watch for media element size changes
        let resizeObserver: ResizeObserver | null = null;
        if (window.ResizeObserver) {
            resizeObserver = new ResizeObserver((entries) => {
                // Debounce rapid resize events
                requestAnimationFrame(() => {
                    if (mediaRef.current && containerRef.current) {
                        setupCanvas();
                    }
                });
            });
            resizeObserver.observe(mediaElement);
            resizeObserver.observe(containerElement);
        }

        // Set up MutationObserver to watch for style changes that might affect positioning
        let mutationObserver: MutationObserver | null = null;
        if (window.MutationObserver) {
            mutationObserver = new MutationObserver((mutations) => {
                let shouldUpdate = false;
                mutations.forEach((mutation) => {
                    if (
                        mutation.type === "attributes" &&
                        (mutation.attributeName === "style" || mutation.attributeName === "class")
                    ) {
                        shouldUpdate = true;
                    }
                });

                if (shouldUpdate) {
                    requestAnimationFrame(() => {
                        if (mediaRef.current && containerRef.current) {
                            setupCanvas();
                        }
                    });
                }
            });

            mutationObserver.observe(mediaElement, {
                attributes: true,
                attributeFilter: ["style", "class"]
            });
            mutationObserver.observe(containerElement, {
                attributes: true,
                attributeFilter: ["style", "class"]
            });
        }

        // Window resize handler
        window.addEventListener("resize", handleMediaLoadOrResize);

        // Intersection observer to handle cases where element comes into/out of view
        let intersectionObserver: IntersectionObserver | null = null;
        if (window.IntersectionObserver) {
            intersectionObserver = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        requestAnimationFrame(() => {
                            if (mediaRef.current && containerRef.current) {
                                setupCanvas();
                            }
                        });
                    }
                });
            });
            intersectionObserver.observe(mediaElement);
        }

        return () => {
            if (mediaElement) {
                mediaElement.removeEventListener(eventName, handleMediaLoadOrResize);
            }
            window.removeEventListener("resize", handleMediaLoadOrResize);

            if (resizeObserver) {
                resizeObserver.disconnect();
            }

            if (mutationObserver) {
                mutationObserver.disconnect();
            }

            if (intersectionObserver) {
                intersectionObserver.disconnect();
            }
        };
    }, [mediaRef, containerRef, setupCanvas]);

    // Effect to handle forced realignment from parent component
    useEffect(() => {
        if (forceRealign !== undefined && forceRealign > 0) {
            requestAnimationFrame(() => {
                if (mediaRef.current && containerRef.current) {
                    setupCanvas();
                }
            });
        }
    }, [forceRealign, setupCanvas, mediaRef, containerRef]);

    // Clear preview annotation when selected object changes (e.g., switching between labels)
    useEffect(() => {
        setPreviewAnnotation(null);
        setDrawingState({ action: "idle" });
    }, [selectedObject]);

    // --- Drawing Logic ---
    const drawGuideLines = useCallback(
        (ctx: CanvasRenderingContext2D, canvasWidth: number, canvasHeight: number) => {
            // console.log("drawGuideLines", isViewOnly || !mousePosition);
            if (isViewOnly || !mousePosition) return;
            ctx.save();
            ctx.setLineDash([4, 2]);
            ctx.strokeStyle = guideLineColor;
            ctx.lineWidth = 1;

            // Horizontal line
            ctx.beginPath();
            ctx.moveTo(0, mousePosition.y);
            ctx.lineTo(canvasWidth, mousePosition.y);
            ctx.stroke();

            // Vertical line
            ctx.beginPath();
            ctx.moveTo(mousePosition.x, 0);
            ctx.lineTo(mousePosition.x, canvasHeight);
            ctx.stroke();

            ctx.restore();
        },
        [mousePosition, isViewOnly, guideLineColor]
    );

    // Note: drawOptions is created fresh in redrawCanvas to ensure it's always current
    // Memoizing it here doesn't work well because refs don't trigger re-renders when .current changes

    // Memoize selected annotation ID to avoid object reference issues
    const selectedAnnotationId = useMemo(
        () => selectedAnnotation?.id || null,
        [selectedAnnotation]
    );

    // Memoize whether selected annotation is in current frame
    const isSelectedAnnotationInCurrentFrame = useMemo(() => {
        return (
            selectedAnnotationId &&
            (annotationsToDrawScaled as unknown as ReadonlyArray<T>).some(
                (anno) => anno.id === selectedAnnotationId
            )
        );
    }, [selectedAnnotationId, annotationsToDrawScaled]);

    // Cache rects outside of redraw to avoid expensive getBoundingClientRect calls
    const drawOptionsRef = useRef({
        imageRect: null as DOMRect | null,
        containerRect: null as DOMRect | null
    });

    const updateDrawOptions = useCallback(() => {
        const canvas = canvasRef.current;
        const mediaElement = mediaRef.current;
        if (!canvas || !mediaElement) return;

        drawOptionsRef.current = {
            imageRect: mediaElement.getBoundingClientRect(),
            containerRect: canvas.getBoundingClientRect()
        };
    }, [mediaRef]);

    const redrawCanvas = useCallback(() => {
        const canvas = canvasRef.current;
        const mediaElement = mediaRef.current;
        if (!canvas || !mediaElement || !renderedImageGeom) return;
        if (renderedImageGeom.width === 0 || renderedImageGeom.height === 0) {
            return;
        }
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        // Use cached rects instead of calling getBoundingClientRect every frame
        const drawOptions = drawOptionsRef.current;

        const labelPadding = parseInt(canvas.dataset.labelPadding || "0", 10);
        const dpr = window.devicePixelRatio || 1;

        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.scale(dpr, dpr);
        ctx.translate(0, labelPadding);

        ctx.save();

        (annotationsToDrawScaled as unknown as ReadonlyArray<T>).forEach((anno) => {
            // In text prompt mode, allow selection/hover styling
            const canShowInteractiveState = true;
            const isSelected = selectedAnnotationId === anno.id && canShowInteractiveState;
            const isHovered = hoveredAnnotationId === anno.id && canShowInteractiveState;
            const isLabelHovered = labelHoveredAnnotationId === anno.id && canShowInteractiveState;
            // Use editingAnnotationId to control hideLabel for strategy
            const isBeingEditedByParent = editingAnnotationId === anno.id && !isViewOnly;
            // Dim all annotations except the hovered one when hovering over a thumbnail
            const shouldDim = hoveredAnnotationId && anno.id !== hoveredAnnotationId;

            if (shouldDim) {
                ctx.save();
                ctx.globalAlpha = 0.3; // Dim to 30% opacity
            }

            if (
                !(
                    isSelected &&
                    (drawingState.action === "moving" || drawingState.action === "resizing")
                )
            ) {
                strategy.draw(ctx, anno, {
                    ...drawOptions,
                    isSelected,
                    isHovered,
                    isLabelHovered,
                    hideLabel: forceHideLabels || isBeingEditedByParent || shouldDim
                });
            }

            if (shouldDim) {
                ctx.restore();
            }
        });

        if (previewAnnotation && !isViewOnly) {
            strategy.draw(ctx, previewAnnotation, {
                ...drawOptions,
                isSelected: true,
                hideLabel: forceHideLabels
            });
        }

        if (
            isSelectedAnnotationInCurrentFrame &&
            selectedAnnotation && // Still need this for type guarding selectedAnnotation itself
            drawingState.action === "idle" &&
            editingAnnotationId !== selectedAnnotationId &&
            !isViewOnly
        ) {
            // Use the scaled annotation for handle positions to match the drawn box
            const scaledSelected = (annotationsToDrawScaled as unknown as ReadonlyArray<T>).find(
                (a) => a.id === selectedAnnotationId
            );

            if (scaledSelected) {
                strategy.drawResizeHandles(
                    ctx,
                    scaledSelected,
                    drawOptions.imageRect,
                    drawOptions.containerRect
                );
            }
        }
        drawGuideLines(ctx, renderedImageGeom.width, renderedImageGeom.height);

        // Apply watermark if available
        if (includeWatermark && watermarkCanvas) {
            applyWatermarkToCanvas(
                ctx,
                watermarkCanvas,
                renderedImageGeom.width,
                renderedImageGeom.height
            );
        }

        // Restore the canvas context to remove zoom transformation
        ctx.restore();
    }, [
        // Only include dependencies that actually affect what's drawn
        renderedImageGeom,
        annotationsToDrawScaled,
        strategy,
        selectedAnnotationId,
        hoveredAnnotationId,
        labelHoveredAnnotationId,
        previewAnnotation,
        drawingState.action, // Only action, not whole drawingState object
        editingAnnotationId,
        isViewOnly,
        forceHideLabels,
        includeWatermark,
        watermarkCanvas,
        drawGuideLines,
        isSelectedAnnotationInCurrentFrame,
        mediaRef,
        selectedAnnotation
    ]);

    // Create watermark canvas when needed
    useEffect(() => {
        if (
            includeWatermark &&
            renderedImageGeom &&
            renderedImageGeom.width > 0 &&
            renderedImageGeom.height > 0
        ) {
            createWatermark(renderedImageGeom.width, renderedImageGeom.height, {
                qrUrl: watermarkQrUrl
            }).then((canvas) => {
                setWatermarkCanvas(canvas);
            });
        } else {
            setWatermarkCanvas(null);
        }
    }, [includeWatermark, watermarkQrUrl, renderedImageGeom]);

    // Use useLayoutEffect for synchronous drawing to eliminate visual lag
    // Since segment changes are already throttled, we don't need RAF batching
    useLayoutEffect(() => {
        redrawCanvas();
    }, [redrawCanvas]);

    // Only update draw options when canvas/video geometry changes, not on every frame
    useLayoutEffect(() => {
        updateDrawOptions();
    }, [updateDrawOptions, renderedImageGeom]);

    // Cleanup mask cache on unmount
    useEffect(() => {
        return clearMaskCache;
    }, []);

    // Memoize annotations that need thumbnails to avoid infinite loops
    const annotationsNeedingThumbnails = useMemo(() => {
        return currentFrameAnnotations.filter(
            (annotation) => !annotation.draft && !annotation.thumbnailDataUrl
        );
    }, [currentFrameAnnotations]);

    // Memoize predictions that need thumbnails
    const predictionsNeedingThumbnails = useMemo(() => {
        const thumbnailCandidateIds = new Set(
            visibleCurrentFramePredictions.map((p) => p.detection_id)
        );
        return currentFramePredictions.filter(
            (prediction) =>
                !prediction.thumbnailDataUrl && thumbnailCandidateIds.has(prediction.detection_id)
        );
    }, [currentFramePredictions, visibleCurrentFramePredictions]);

    // Memoized function to generate thumbnails for annotations
    const generateAnnotationThumbnails = useCallback(async () => {
        if (annotationsNeedingThumbnails.length === 0) return;

        const updatedAnnotations: any[] = [];
        let hasUpdates = false;

        for (const annotation of annotationsNeedingThumbnails) {
            try {
                const thumbnailUrl = await generateThumbnailForCurrentFrame(annotation);
                updatedAnnotations.push({
                    ...annotation,
                    thumbnailDataUrl: thumbnailUrl ?? undefined
                });
                hasUpdates = true;
            } catch (error) {
                updatedAnnotations.push(annotation);
            }
        }

        if (hasUpdates) {
            setCurrentFrameAnnotations((prev: any[]) =>
                prev.map((anno) => {
                    const updated = updatedAnnotations.find((u) => u.id === anno.id);
                    return updated || anno;
                })
            );
        }
    }, [
        annotationsNeedingThumbnails,
        generateThumbnailForCurrentFrame,
        setCurrentFrameAnnotations
    ]);

    // Memoized function to generate thumbnails for predictions
    const generatePredictionThumbnails = useCallback(async () => {
        if (predictionsNeedingThumbnails.length === 0) return;

        const updatedPredictions: any[] = [];
        let hasUpdates = false;

        for (const prediction of predictionsNeedingThumbnails) {
            try {
                const thumbnailUrl = await generateThumbnailForCurrentFrame(prediction);
                updatedPredictions.push({
                    ...prediction,
                    thumbnailDataUrl: thumbnailUrl ?? undefined
                });
                hasUpdates = true;
            } catch (error) {
                console.error("Error generating thumbnail for prediction", error);
                updatedPredictions.push(prediction);
            }
        }

        if (hasUpdates) {
            setCurrentFramePredictions((prev: any[]) =>
                prev.map((pred) => {
                    const updated = updatedPredictions.find(
                        (u) => u.detection_id === pred.detection_id
                    );
                    return updated || pred;
                })
            );
        }
    }, [
        predictionsNeedingThumbnails,
        generateThumbnailForCurrentFrame,
        setCurrentFramePredictions
    ]);

    // Track when thumbnail arrays actually change (not just their length)
    const prevAnnotationsNeedingThumbsRef = useRef(annotationsNeedingThumbnails);
    const annotationsNeedingThumbsChanged =
        prevAnnotationsNeedingThumbsRef.current !== annotationsNeedingThumbnails;
    if (annotationsNeedingThumbsChanged) {
        prevAnnotationsNeedingThumbsRef.current = annotationsNeedingThumbnails;
    }

    const prevPredictionsNeedingThumbsRef = useRef(predictionsNeedingThumbnails);
    const predictionsNeedingThumbsChanged =
        prevPredictionsNeedingThumbsRef.current !== predictionsNeedingThumbnails;
    if (predictionsNeedingThumbsChanged) {
        prevPredictionsNeedingThumbsRef.current = predictionsNeedingThumbnails;
    }

    // Main thumbnail generation effect - now only triggers when there are actually items needing thumbnails
    useEffect(() => {
        if (
            onlyShowPredictions ||
            thumbnailGenerationRef.current ||
            !originalMediaDimensions ||
            !renderedImageGeom ||
            renderedImageGeom.width === 0 ||
            renderedImageGeom.height === 0
        ) {
            return;
        }

        const generateThumbnails = async () => {
            thumbnailGenerationRef.current = true;

            try {
                await Promise.all([generateAnnotationThumbnails(), generatePredictionThumbnails()]);
            } finally {
                thumbnailGenerationRef.current = false;
            }
        };

        // Only run if there are annotations or predictions that need thumbnails
        if (annotationsNeedingThumbnails.length > 0 || predictionsNeedingThumbnails.length > 0) {
            generateThumbnails();
        }
    }, [
        onlyShowPredictions,
        originalMediaDimensions,
        renderedImageGeom,
        annotationsNeedingThumbnails,
        annotationsNeedingThumbsChanged,
        predictionsNeedingThumbnails,
        predictionsNeedingThumbsChanged,
        generateAnnotationThumbnails,
        generatePredictionThumbnails
    ]);

    const adjustForLabelPadding = useCallback((rawY: number): number => {
        const canvas = canvasRef.current;
        if (!canvas) return rawY;
        const labelPadding = parseInt(canvas.dataset.labelPadding || "0", 10);
        return rawY - labelPadding;
    }, []);

    const isPointOutsideBounds = useCallback(
        (point: Point): boolean => {
            if (!renderedImageGeom) return true;
            return point.y < 0 || point.y >= renderedImageGeom.height;
        },
        [renderedImageGeom]
    );

    const getPointFromEvent = useCallback(
        (e: React.MouseEvent<HTMLCanvasElement>): Point => {
            const canvas = canvasRef.current;
            if (!canvas || !renderedImageGeom) return { x: 0, y: 0 };
            const rect = canvas.getBoundingClientRect();

            const rawX = e.clientX - rect.left;
            const rawY = e.clientY - rect.top;

            const canvasX = rawX / zoomLevel;
            const canvasY = rawY / zoomLevel;

            const adjustedY = adjustForLabelPadding(canvasY);

            return { x: canvasX, y: adjustedY };
        },
        [renderedImageGeom, zoomLevel, adjustForLabelPadding]
    );

    const handleMouseDown = useCallback(
        (e: React.MouseEvent<HTMLCanvasElement>) => {
            // Middle mouse button (scroll wheel click) - start panning
            if (e.button === 1) {
                e.preventDefault();
                setIsMiddleMousePanning(true);
                setMiddleMousePanStart({ x: e.clientX, y: e.clientY });
                setCursor("grabbing");
                return;
            }

            const point = getPointFromEvent(e);
            const media = mediaRef.current;
            const canvas = canvasRef.current;
            if (!media || !canvas) return;

            if (isPointOutsideBounds(point)) return;

            const clickedAnnotation = findAnnotationAtPoint(point);

            // In view-only mode, allow clicking on existing boxes but prevent drawing/editing
            if (isViewOnly) {
                if (clickedAnnotation) {
                    handleAnnotationSelection(clickedAnnotation);
                } else {
                    handleEmptySpaceClick();
                }
                return;
            }

            // Let the strategy determine the primary mouse down action (draw, move, resize, or idle)
            const strategyResult = strategy.handleMouseDown(
                point,
                media,
                clickedAnnotation,
                annotationsToDrawScaled as unknown as ReadonlyArray<T>,
                clickedAnnotation ? null : labelToUse
            );

            if (strategyResult.drawingStateUpdate) {
                setDrawingState(
                    (prev) =>
                        ({
                            ...prev,
                            ...(strategyResult.drawingStateUpdate || {}),
                            action: strategyResult.drawingStateUpdate!.action || prev.action
                        }) as DrawingState<T>
                );
            }
            if (strategyResult.newAnnotation) {
                setPreviewAnnotation(strategyResult.newAnnotation);
            }
            if (strategyResult.stopPropagation) {
                e.stopPropagation();
            }

            // Handle selection and potential edit request if not initiating a drag operation
            if (clickedAnnotation) {
                handleAnnotationSelection(clickedAnnotation);

                if (!strategyResult.isDragInitiation) {
                    const resizeHandle = strategy.getResizeHandleAtPoint(point, clickedAnnotation);
                    const isLabelClick =
                        !resizeHandle &&
                        (strategy.isPointInAnnotationLabel?.(point, clickedAnnotation) || false);
                    setShouldShowAnnotationInput(isLabelClick);
                }
            } else if (!strategyResult.isDragInitiation) {
                handleEmptySpaceClick();
            }
        },
        [
            findAnnotationAtPoint,
            getPointFromEvent,
            handleAnnotationSelection,
            handleEmptySpaceClick,
            mediaRef,
            isViewOnly,
            strategy,
            annotationsToDrawScaled,
            labelToUse,
            setShouldShowAnnotationInput,
            isPointOutsideBounds
        ]
    );

    // Extract drawingState.action to avoid recreating callback on every state change
    const drawingAction = drawingState.action;

    const handleMouseMove = useCallback(
        (e: React.MouseEvent<HTMLCanvasElement>) => {
            // Middle mouse panning is handled by the global event listener
            // to ensure it continues even when mouse moves outside canvas
            if (isMiddleMousePanning) {
                return;
            }

            const point = getPointFromEvent(e);
            const mediaElement = mediaRef.current;
            if (!mediaElement || !renderedImageGeom) return;

            if (isPointOutsideBounds(point)) {
                setMousePosition(null);
                setCursor("default");
                return;
            }

            // In view-only mode, allow hovering for dimming effect but no drawing/editing
            if (isViewOnly) {
                setMousePosition(point);
                const annotationUnderMouse = findAnnotationAtPoint(point);
                updateHoverState(
                    annotationUnderMouse,
                    annotationUnderMouse ? "pointer" : "default"
                );
                return;
            }

            // Always update mouse position for guide lines (even when not in view-only mode)
            setMousePosition(point);

            const result = strategy.handleMouseMove(
                point,
                mediaElement,
                drawingState,
                annotationsToDrawScaled as unknown as ReadonlyArray<T>,
                { width: renderedImageGeom.width, height: renderedImageGeom.height } // Safe now due to guard above
            );

            if (result.drawingStateUpdate) {
                setDrawingState(
                    (prev) =>
                        ({
                            ...prev,
                            ...(result.drawingStateUpdate || {}), // Check for undefined
                            action: result.drawingStateUpdate!.action || prev.action
                        }) as DrawingState<T>
                );
            }
            if (result.previewAnnotationUpdate !== undefined) {
                setPreviewAnnotation(result.previewAnnotationUpdate);
            }
            // Update hover from mouse movement and sync to context for dimming effect
            if (result.hoveredAnnotation !== undefined) {
                const hoverId = result.hoveredAnnotation?.id || null;
                setHoveredAnnotationId(hoverId);
            }
            // Handle cursor logic with priority for resize handles
            if (drawingAction === "idle") {
                let newCursor = "crosshair";
                const annotationUnderMouse = (
                    annotationsToDrawScaled as unknown as ReadonlyArray<T>
                ).find((anno) => strategy.isPointInAnnotation(point, anno));

                if (annotationUnderMouse) {
                    const resizeHandle = strategy.getResizeHandleAtPoint(
                        point,
                        annotationUnderMouse
                    );
                    if (resizeHandle) {
                        // Set cursor based on resize handle direction
                        switch (resizeHandle) {
                            case "nw":
                            case "se":
                                newCursor = "nwse-resize";
                                break;
                            case "ne":
                            case "sw":
                                newCursor = "nesw-resize";
                                break;
                            case "n":
                            case "s":
                                newCursor = "ns-resize";
                                break;
                            case "e":
                            case "w":
                                newCursor = "ew-resize";
                                break;
                            default:
                                newCursor = "move";
                        }
                    } else {
                        // Check if hovering over label text for text cursor
                        const isOverLabel = strategy.isPointInAnnotationLabel?.(
                            point,
                            annotationUnderMouse
                        );

                        newCursor = isOverLabel ? "text" : "pointer";
                        // Track label hover state for visual highlighting
                        setLabelHoveredAnnotationId(isOverLabel ? annotationUnderMouse.id : null);
                    }
                } else {
                    // Not over any annotation, clear label hover
                    setLabelHoveredAnnotationId(null);
                }
                setCursor(newCursor);
            } else if (result.cursor) {
                setCursor(result.cursor);
            }
        },
        [
            findAnnotationAtPoint,
            getPointFromEvent,
            updateHoverState,
            mediaRef,
            renderedImageGeom,
            isViewOnly,
            strategy,
            drawingState,
            annotationsToDrawScaled,
            drawingAction,
            isMiddleMousePanning,
            setHoveredAnnotationId,
            isPointOutsideBounds
        ]
    );

    const handleMouseUp = useCallback(
        async (e: React.MouseEvent<HTMLCanvasElement>) => {
            // Stop middle mouse button panning
            if (e.button === 1) {
                setIsMiddleMousePanning(false);
                setMiddleMousePanStart(null);
                // Set cursor to crosshair immediately (default for editable canvas)
                // The next mouse move will update it based on what's under the cursor
                if (!isViewOnly && !onlyShowPredictions) {
                    setCursor("crosshair");
                }
                return;
            }

            if (isViewOnly || onlyShowPredictions) return;

            // Prevent the global mouse up handler from also firing (only for real React events)
            if (e.stopPropagation) {
                e.stopPropagation();
            }
            const point = getPointFromEvent(e);
            const mediaElement = mediaRef.current;
            if (!mediaElement) return;

            const strategyResult = strategy.handleMouseUp(
                point, // This point is the mouse position at mouseup
                mediaElement,
                drawingState, // Contains original startPoint and clamped currentPoint for drawing
                labelToUse
            );

            let finalNewAnnotationWithThumb: Annotation | null = null;

            // Handle new annotation creation
            if (drawingState.action === "drawing") {
                // strategyResult.newAnnotation is the definitive source from BoundingBoxStrategy.
                // It will be null if the drag was too short or the box too small.
                const newAnnotationFromStrategy =
                    strategyResult.newAnnotation as BoundingBox | null;

                if (newAnnotationFromStrategy) {
                    // Use the annotation data returned by the strategy.
                    // It has already been validated for size/drag distance.
                    let baseAnnotation: Annotation = {
                        ...newAnnotationFromStrategy, // Contains id, x, y, width, height, label
                        id: newAnnotationFromStrategy.id || uuidv4(), // Ensure ID exists
                        // Ensure label and frameId are correctly set, selectedObject might be more current for label
                        label: labelToUse, // Override label if needed
                        fileId: currentMedia?.file.id!,
                        frameId: currentMedia?.frame?.id!
                    };

                    // ---- NEW: convert the annotation from canvas space to original media space ----
                    // Use renderedImageGeom (un-zoomed canvas dimensions) as the source,
                    // not mediaRect which includes CSS transform zoom
                    if (originalMediaDimensions && renderedImageGeom) {
                        const { scaledRect } = scaleRect({
                            source: {
                                width: renderedImageGeom.width,
                                height: renderedImageGeom.height
                            },
                            target: originalMediaDimensions,
                            rect: baseAnnotation
                        });

                        baseAnnotation = { ...baseAnnotation, ...scaledRect };
                    }

                    // Thumbnail generation logic (now no extra scaling – we are already in original coords)
                    if (
                        originalMediaDimensions &&
                        renderedImageGeom &&
                        renderedImageGeom.width > 0 &&
                        renderedImageGeom.height > 0
                    ) {
                        const annotationForThumbnail = {
                            x: baseAnnotation.x,
                            y: baseAnnotation.y,
                            width: baseAnnotation.width,
                            height: baseAnnotation.height
                        };
                        const thumbnailUrl =
                            await generateThumbnailForCurrentFrame(annotationForThumbnail);
                        finalNewAnnotationWithThumb = {
                            ...baseAnnotation,
                            thumbnailDataUrl: thumbnailUrl ?? undefined
                        };
                    } else {
                        finalNewAnnotationWithThumb = baseAnnotation;
                    }
                } else {
                    // If strategyResult.newAnnotation is null, no new annotation is created.
                    finalNewAnnotationWithThumb = null;
                }
            } else if (
                strategyResult.newAnnotation &&
                (drawingState.action === "moving" || drawingState.action === "resizing")
            ) {
                // This path is less common for move/resize, as they typically use modifiedAnnotationId.
                // However, if a strategy *did* return a newAnnotation object during move/resize (e.g. for copy-paste),
                // handle it similarly. For BoundingBoxStrategy, this branch is unlikely to be hit.
                let newAnnotationFromStrategy =
                    strategyResult.newAnnotation as unknown as BoundingBox;
                let baseAnnotation: Annotation = {
                    ...newAnnotationFromStrategy,
                    label: labelToUse,
                    fileId: currentMedia?.file.id!,
                    frameId: currentMedia?.frame?.id!
                };
                // ... (thumbnail logic can be repeated or refactored if this path becomes common) ...
                if (
                    originalMediaDimensions &&
                    renderedImageGeom &&
                    renderedImageGeom.width > 0 &&
                    renderedImageGeom.height > 0
                ) {
                    const { scaledRect: annotationForThumbnail } = scaleRect({
                        source: renderedImageGeom,
                        target: originalMediaDimensions,
                        rect: baseAnnotation
                    });

                    const thumbnailUrl =
                        await generateThumbnailForCurrentFrame(annotationForThumbnail);

                    finalNewAnnotationWithThumb = {
                        ...baseAnnotation,
                        thumbnailDataUrl: thumbnailUrl ?? undefined
                    };
                } else {
                    finalNewAnnotationWithThumb = baseAnnotation;
                }
            }

            if (finalNewAnnotationWithThumb) {
                setCurrentFrameAnnotations((prev: Annotation[]) => [
                    ...prev,
                    { ...finalNewAnnotationWithThumb, modifiedAt: Date.now() }
                ]);
                setSelectedAnnotation(finalNewAnnotationWithThumb as unknown as T);
                setPreviouslySelectedObject(finalNewAnnotationWithThumb?.label ?? null);

                // Only mark the label as edited if the user explicitly selected it
                // (via selectedObject or previouslySelectedObject), not when it falls back
                // to the first prompt label. This prevents auto-assigned labels from being
                // marked as edited when the user draws a new box for a different label.
                // The AnnotationLabelInput will handle marking the label as edited after
                // the user confirms their label choice.
                const drawnLabel = finalNewAnnotationWithThumb.label;
                const userExplicitlySelectedLabel = selectedObject || previouslySelectedObject;
                if (drawnLabel && promptLabels.has(drawnLabel) && userExplicitlySelectedLabel) {
                    markLabelAsEdited(drawnLabel);
                }

                // Track box drawing event
                track({
                    event: AnalyticsFlowEvent.ANNOTATION_BOX_DRAWN,
                    data: {
                        annotationCount: currentFrameAnnotations.length + 1, // +1 for the new annotation
                        hasTextPrompts: false, // This is for box drawing, not text prompts
                        textPrompts: [],
                        boxDimensions: {
                            width: finalNewAnnotationWithThumb.width,
                            height: finalNewAnnotationWithThumb.height
                        },
                        label: finalNewAnnotationWithThumb.label
                    }
                });

                if (
                    drawingState.action === "drawing" && // Check original drawing state
                    onAnnotationEditRequest &&
                    canvasRef.current
                ) {
                    const currentBox = finalNewAnnotationWithThumb;
                    const boxTlxCanvas = currentBox.x - currentBox.width / 2;
                    const boxTlyCanvas = currentBox.y - currentBox.height / 2;
                    const eventDetails = {
                        annotation: finalNewAnnotationWithThumb as unknown as T,
                        boxCanvasTop: boxTlyCanvas,
                        boxCanvasLeft: boxTlxCanvas,
                        canvasBoundingRect: canvasRef.current.getBoundingClientRect()
                    };
                    onAnnotationEditRequest(eventDetails);
                }
            } else if (
                strategyResult.modifiedAnnotationId && // strategyResult.modifiedAnnotationId
                (drawingState.action === "moving" || drawingState.action === "resizing") &&
                previewAnnotation // Use the clamped previewAnnotation for modifications
            ) {
                let modifiedAnnotation: Annotation | null = null;
                const currentPreviewAsBox = previewAnnotation as unknown as BoundingBox;
                modifiedAnnotation = {
                    ...currentPreviewAsBox, // Use the preview, which is clamped
                    id: strategyResult.modifiedAnnotationId!, // Use ID from strategy result
                    label: currentPreviewAsBox.label,
                    fileId: currentMedia?.file.id!,
                    frameId: currentMedia?.frame?.id!,
                    draft: false // User edited this annotation, so it's no longer a draft
                };

                // ---- NEW: rescale the modified annotation into original media coordinates ----
                // Use renderedImageGeom (un-zoomed canvas dimensions) as the source,
                // not mediaRect which includes CSS transform zoom
                if (originalMediaDimensions && renderedImageGeom) {
                    const { scaledRect } = scaleRect({
                        source: {
                            width: renderedImageGeom.width,
                            height: renderedImageGeom.height
                        },
                        target: originalMediaDimensions,
                        rect: modifiedAnnotation
                    });

                    modifiedAnnotation = { ...modifiedAnnotation, ...scaledRect };
                }

                // Generate/refresh thumbnail using already-scaled coordinates
                if (
                    modifiedAnnotation &&
                    originalMediaDimensions &&
                    renderedImageGeom &&
                    renderedImageGeom.width > 0 &&
                    renderedImageGeom.height > 0 &&
                    mediaElement
                ) {
                    const annotationForThumbnail = {
                        x: modifiedAnnotation.x,
                        y: modifiedAnnotation.y,
                        width: modifiedAnnotation.width,
                        height: modifiedAnnotation.height
                    };
                    const thumbnailUrl =
                        await generateThumbnailForCurrentFrame(annotationForThumbnail);
                    modifiedAnnotation = {
                        ...modifiedAnnotation,
                        thumbnailDataUrl: thumbnailUrl ?? undefined
                    };
                }

                if (modifiedAnnotation) {
                    setCurrentFrameAnnotations((prevFrameAnnotations: Annotation[]) =>
                        prevFrameAnnotations.map((a) =>
                            a.id === strategyResult.modifiedAnnotationId ? modifiedAnnotation : a
                        )
                    );
                    setSelectedAnnotation(modifiedAnnotation as unknown as T);
                    setPreviouslySelectedObject(modifiedAnnotation?.label ?? null);

                    // Mark this label as having user edits (moved/resized)
                    if (modifiedAnnotation?.label) {
                        markLabelAsEdited(modifiedAnnotation.label);
                    }
                }
            }

            // Reset drawing state based on strategy's mouse up result or default to idle
            if (strategyResult.drawingStateUpdate) {
                setDrawingState(
                    (prev) =>
                        ({
                            ...prev,
                            ...(strategyResult.drawingStateUpdate || {}),
                            action: strategyResult.drawingStateUpdate!.action || prev.action
                        }) as DrawingState<T>
                );
            } else {
                setDrawingState({ action: "idle" });
            }
            setPreviewAnnotation(null); // Clear the preview annotation
        },
        [
            currentMedia?.file.id,
            currentMedia?.frame?.id,
            isViewOnly,
            onlyShowPredictions,
            getPointFromEvent,
            mediaRef,
            strategy,
            drawingState,
            previewAnnotation,
            originalMediaDimensions,
            renderedImageGeom,
            generateThumbnailForCurrentFrame,
            setCurrentFrameAnnotations,
            setPreviouslySelectedObject,
            track,
            currentFrameAnnotations.length,
            onAnnotationEditRequest,
            labelToUse,
            markLabelAsEdited,
            selectedObject,
            previouslySelectedObject
        ]
    );

    const handleMouseLeave = useCallback(
        (e: React.MouseEvent<HTMLCanvasElement>) => {
            setMousePosition(null); // Hide guide lines

            if (isViewOnly || onlyShowPredictions || drawingAction === "idle") {
                // Clear canvas hover
                setHoveredAnnotationId(null);
                if (!isViewOnly && !onlyShowPredictions) setCursor("crosshair");
                else if (isViewOnly || onlyShowPredictions) setCursor("not-allowed");
                return;
            }

            const canvas = canvasRef.current;
            const mediaElement = mediaRef.current; // Get mediaElement

            if (!canvas || !mediaElement || !renderedImageGeom) return;

            // Calculate the ACTUAL mouse position relative to the canvas origin
            const canvasRect = canvas.getBoundingClientRect();

            // Calculate mouse position relative to canvas bounding rect (which is scaled by CSS transform)
            const rawX = e.clientX - canvasRect.left;
            const rawY = e.clientY - canvasRect.top;

            // Since the canvas uses CSS transform for zoom, we need to convert screen coordinates
            // to canvas coordinates by dividing by the zoom level
            const actualPoint: Point = { x: rawX / zoomLevel, y: rawY / zoomLevel };

            // Call strategy.handleMouseMove with the actual (potentially off-canvas) point
            // and the canvas geometry for clamping the output shape.
            const strategyResult = strategy.handleMouseMove(
                actualPoint,
                mediaElement,
                drawingState,
                annotationsToDrawScaled as unknown as ReadonlyArray<T>,
                { width: renderedImageGeom.width, height: renderedImageGeom.height }
            );

            // Update canvas state based on strategy result
            if (strategyResult.drawingStateUpdate) {
                setDrawingState(
                    (prev) =>
                        ({
                            ...prev,
                            ...(strategyResult.drawingStateUpdate || {}),
                            action: strategyResult.drawingStateUpdate!.action || prev.action
                        }) as DrawingState<T>
                );
            }
            if (strategyResult.previewAnnotationUpdate !== undefined) {
                setPreviewAnnotation(strategyResult.previewAnnotationUpdate);
            }
            if (strategyResult.hoveredAnnotation !== undefined) {
                const hoverId = strategyResult.hoveredAnnotation?.id || null;
                setHoveredAnnotationId(hoverId);
            }
            if (strategyResult.cursor) {
                setCursor(strategyResult.cursor);
            }
        },
        [
            isViewOnly,
            onlyShowPredictions,
            drawingState,
            drawingAction,
            canvasRef,
            mediaRef,
            renderedImageGeom,
            strategy,
            annotationsToDrawScaled,
            setCursor,
            setHoveredAnnotationId,
            setDrawingState,
            setPreviewAnnotation
        ]
    );

    const handleDoubleClick = useCallback(
        (e: React.MouseEvent<HTMLCanvasElement>) => {
            if (isViewOnly || onlyShowPredictions) return;

            const point = getPointFromEvent(e);
            const clickedAnnotation = findAnnotationAtPoint(point);

            if (
                clickedAnnotation &&
                onAnnotationEditRequest &&
                canvasRef.current &&
                containerRef.current
            ) {
                handleAnnotationSelection(clickedAnnotation);

                // Find the original unscaled annotation from currentFrameAnnotations
                // The clickedAnnotation is scaled, but we need the original coordinates
                const originalAnnotation = currentFrameAnnotations.find(
                    (anno: Annotation) => anno.id === clickedAnnotation.id
                ) as BoundingBox | undefined;

                if (originalAnnotation) {
                    const boxTlxCanvas = originalAnnotation.x - originalAnnotation.width / 2;
                    const boxTlyCanvas = originalAnnotation.y - originalAnnotation.height / 2;
                    const eventDetails: AnnotationEditRequestDetails<T> = {
                        annotation: originalAnnotation as unknown as T,
                        boxCanvasTop: boxTlyCanvas,
                        boxCanvasLeft: boxTlxCanvas,
                        canvasBoundingRect: canvasRef.current.getBoundingClientRect()
                    };
                    onAnnotationEditRequest(eventDetails);
                }
            }
        },
        [
            isViewOnly,
            onlyShowPredictions,
            getPointFromEvent,
            findAnnotationAtPoint,
            handleAnnotationSelection,
            onAnnotationEditRequest,
            containerRef,
            currentFrameAnnotations
        ]
    );

    const handleWheel = useCallback(
        (e: React.WheelEvent<HTMLDivElement>) => {
            e.preventDefault();

            if (e.ctrlKey || e.metaKey) {
                // Handle zoom on Ctrl/Cmd + wheel
                const canvas = canvasRef.current;
                if (!canvas) return;

                const rect = canvas.getBoundingClientRect();
                // Calculate coordinates relative to the canvas
                const x = e.clientX - rect.left;
                const y = e.clientY - rect.top;

                // Calculate zoom factor based on wheel delta
                const delta = e.deltaY;
                const zoomFactor = delta > 0 ? 0.9 : 1.1;

                // Get current zoom from context
                const newZoom = Math.max(0.1, Math.min(5.0, contextZoomLevel * zoomFactor));

                setZoom(newZoom, x, y);
            } else {
                // Handle panning on regular wheel
                const panSpeed = 1.0;
                pan(-e.deltaX * panSpeed, -e.deltaY * panSpeed);
            }
        },
        [contextZoomLevel, setZoom, pan]
    );

    // --- Keyboard Shortcuts ---
    // Escape to cancel/deselect
    const handleEscape = useCallback(() => {
        // If an annotation label is being edited, let the input handle Escape first.
        // The input's onClose should set editingAnnotationId to null.
        if (editingAnnotationId === null) {
            setDrawingState({ action: "idle" });
            setSelectedAnnotation(null);
            setPreviewAnnotation(null);
        }
    }, [editingAnnotationId, setDrawingState, setSelectedAnnotation, setPreviewAnnotation]);

    useKeyboardShortcut({ key: "Escape", handler: handleEscape, disabled: isViewOnly });

    // Delete/Backspace to delete annotation
    const handleDelete = useCallback(() => {
        if (!selectedAnnotation || editingAnnotationId !== null) return;

        // Track annotation deletion from canvas
        track({
            event: AnalyticsFlowEvent.ANNOTATION_OBJECT_DELETED,
            data: {
                appSource: AppSource.RAPID,
                source: "canvas",
                deletionMethod: "keyboard",
                annotationId: selectedAnnotation.id,
                annotationLabel: selectedAnnotation.label,
                annotationDimensions: {
                    width: selectedAnnotation.width,
                    height: selectedAnnotation.height
                },
                remainingAnnotations: currentFrameAnnotations.length - 1
            }
        });

        // Mark this label as having user edits (deletion)
        if (selectedAnnotation.label) {
            markLabelAsEdited(selectedAnnotation.label);
        }

        // Only delete if a box is selected AND its label is not being edited
        setCurrentFrameAnnotations((prev: Annotation[]) =>
            prev.filter((anno) => anno.id !== selectedAnnotation.id)
        );
        setSelectedAnnotation(null);
        setPreviewAnnotation(null); // Clear any lingering preview
    }, [
        selectedAnnotation,
        editingAnnotationId,
        setCurrentFrameAnnotations,
        setSelectedAnnotation,
        setPreviewAnnotation,
        track,
        currentFrameAnnotations.length,
        markLabelAsEdited
    ]);

    useKeyboardShortcut({
        key: ["Delete", "Backspace"],
        handler: handleDelete,
        disabled: isViewOnly || !selectedAnnotation || editingAnnotationId !== null
    });

    // Effect for global mouse listeners during active drawing/moving/resizing
    useEffect(() => {
        if (isViewOnly || drawingState.action === "idle") {
            return; // No need for global listeners if idle or read-only
        }

        const handleGlobalMouseMove = (event: MouseEvent) => {
            // Need to convert global MouseEvent to a synthetic React MouseEvent
            // or directly call the core logic of handleMouseMove with calculated points.
            // For simplicity, we'll simulate what handleMouseMove needs.
            const canvas = canvasRef.current;
            const mediaElement = mediaRef.current; // Required by strategy
            if (!canvas || !mediaElement || !renderedImageGeom) return;

            const canvasRect = canvas.getBoundingClientRect();

            const rawX = event.clientX - canvasRect.left;
            const rawY = event.clientY - canvasRect.top;

            const canvasX = rawX / zoomLevel;
            const canvasY = rawY / zoomLevel;

            const labelPadding = parseInt(canvas.dataset.labelPadding || "0", 10);
            const adjustedY = canvasY - labelPadding;

            const actualPoint: Point = { x: canvasX, y: adjustedY };

            // Call the strategy's mouse move directly or invoke the canvas's handleMouseMove if it handles state updates well.
            // Invoking strategy directly here means we also need to manually update states like previewAnnotation, cursor, etc.
            // based on strategyResult, similar to how handleMouseMove does.
            const strategyResult = strategy.handleMouseMove(
                actualPoint,
                mediaElement,
                drawingState, // current drawing state from canvas
                annotationsToDrawScaled as unknown as ReadonlyArray<T>,
                { width: renderedImageGeom.width, height: renderedImageGeom.height }
            );

            // Update canvas states based on strategy result (mirroring parts of handleMouseMove)
            if (strategyResult.drawingStateUpdate) {
                setDrawingState(
                    (prev) =>
                        ({
                            ...prev,
                            ...(strategyResult.drawingStateUpdate || {}),
                            action: strategyResult.drawingStateUpdate!.action || prev.action
                        }) as DrawingState<T>
                );
            }
            if (strategyResult.previewAnnotationUpdate !== undefined) {
                setPreviewAnnotation(strategyResult.previewAnnotationUpdate);
            }
            // Hovered annotation might not be relevant for global moves, or could be set to null.
            // setHoveredAnnotation(strategyResult.hoveredAnnotation);
            if (strategyResult.cursor) {
                setCursor(strategyResult.cursor);
            }
        };

        const handleGlobalMouseUp = (event: MouseEvent) => {
            // Similar to global mouse move, simulate what handleMouseUp needs.
            const canvas = canvasRef.current;
            if (!canvas) return;

            // Skip if the mouse up happened on the canvas itself - let the canvas handler deal with it
            if (event.target === canvas) {
                return;
            }
            // Create a synthetic event or directly call core logic of handleMouseUp
            // We need to ensure the event passed to handleMouseUp is consistent.
            // For now, we will directly call the existing handleMouseUp, assuming it can handle being called
            // without a direct React event if necessary, or we adapt it.
            // A simple way is to create a minimal event object it can use for getPointFromEvent.
            const syntheticEvent = {
                clientX: event.clientX,
                clientY: event.clientY
                // Ensure other properties that handleMouseUp might expect are available
                // or refactor handleMouseUp to accept just a Point.
            } as React.MouseEvent<HTMLCanvasElement>;

            // Call the canvas's mouse up handler
            // This will finalize the annotation, update state, etc.
            canvasMouseUpHandlerRef.current(syntheticEvent);
        };

        // Need to store handleMouseUp in a ref to avoid stale closures in global event listener
        // if handleMouseUp itself has dependencies that change.
        // For now, let's assume handleMouseUp is stable enough or its deps are well managed.
        // A better approach: use a ref for the canvas's handleMouseUp function.
        // This is a simplified approach assuming stability of handleMouseUp or few deps.

        window.addEventListener("mousemove", handleGlobalMouseMove);
        window.addEventListener("mouseup", handleGlobalMouseUp);

        return () => {
            window.removeEventListener("mousemove", handleGlobalMouseMove);
            window.removeEventListener("mouseup", handleGlobalMouseUp);
        };
    }, [
        isViewOnly,
        onlyShowPredictions,
        drawingState,
        strategy,
        mediaRef,
        renderedImageGeom,
        annotationsToDrawScaled,
        /* Include setters if they are dependencies of the logic above */ setDrawingState,
        setPreviewAnnotation,
        setCursor /* canvasMouseUpHandlerRef.current will be added here */
    ]);

    // Store handleMouseUp in a ref to ensure the global listener gets the latest version
    const canvasMouseUpHandlerRef = useRef(handleMouseUp);
    useEffect(() => {
        canvasMouseUpHandlerRef.current = handleMouseUp;
    }, [handleMouseUp]);

    // Attach wheel event handler to container to enable scrolling over not just the image
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleContainerWheel = (e: WheelEvent) => {
            // Create a synthetic React event for the handler
            const syntheticEvent = {
                preventDefault: () => e.preventDefault(),
                ctrlKey: e.ctrlKey,
                metaKey: e.metaKey,
                deltaX: e.deltaX,
                deltaY: e.deltaY,
                clientX: e.clientX,
                clientY: e.clientY
            } as React.WheelEvent<HTMLDivElement>;

            handleWheel(syntheticEvent);
        };

        // Handle middle mouse button panning from anywhere in the container
        const handleContainerMouseDown = (e: MouseEvent) => {
            if (e.button === 1) {
                e.preventDefault();
                setIsMiddleMousePanning(true);
                setMiddleMousePanStart({ x: e.clientX, y: e.clientY });
                setCursor("grabbing");
            }
        };

        container.addEventListener("wheel", handleContainerWheel, { passive: false });
        container.addEventListener("mousedown", handleContainerMouseDown);

        return () => {
            container.removeEventListener("wheel", handleContainerWheel);
            container.removeEventListener("mousedown", handleContainerMouseDown);
        };
    }, [containerRef, handleWheel]);

    // Global event listeners for middle mouse button panning
    // This ensures panning continues even when mouse moves outside the canvas
    useEffect(() => {
        if (!isMiddleMousePanning) return;

        const handleGlobalMouseMove = (e: MouseEvent) => {
            if (!middleMousePanStart) return;
            const deltaX = e.clientX - middleMousePanStart.x;
            const deltaY = e.clientY - middleMousePanStart.y;
            // Divide by zoom level so pan movement matches mouse movement visually
            // (the transform applies pan after scale, so we need to compensate)
            pan(deltaX / contextZoomLevel, deltaY / contextZoomLevel);
            setMiddleMousePanStart({ x: e.clientX, y: e.clientY });
        };

        const handleGlobalMouseUp = (e: MouseEvent) => {
            if (e.button === 1) {
                setIsMiddleMousePanning(false);
                setMiddleMousePanStart(null);
                // Set cursor to crosshair immediately (default for editable canvas)
                if (!isViewOnly && !onlyShowPredictions) {
                    setCursor("crosshair");
                }
            }
        };

        window.addEventListener("mousemove", handleGlobalMouseMove);
        window.addEventListener("mouseup", handleGlobalMouseUp);

        return () => {
            window.removeEventListener("mousemove", handleGlobalMouseMove);
            window.removeEventListener("mouseup", handleGlobalMouseUp);
        };
    }, [
        isMiddleMousePanning,
        middleMousePanStart,
        pan,
        isViewOnly,
        onlyShowPredictions,
        contextZoomLevel
    ]);

    // --- Touch Event Handlers for Mobile ---
    const [touchState, setTouchState] = useState<{
        action: "idle" | "moving" | "resizing";
        annotation: T | null;
        startPoint: Point | null;
        originalAnnotation: T | null;
        resizeHandle: ResizeHandle | null;
    }>({
        action: "idle",
        annotation: null,
        startPoint: null,
        originalAnnotation: null,
        resizeHandle: null
    });

    const getPointFromNativeTouchEvent = useCallback(
        (e: TouchEvent): Point => {
            const canvas = canvasRef.current;
            if (!canvas || !renderedImageGeom) return { x: 0, y: 0 };
            const rect = canvas.getBoundingClientRect();
            const touch = e.touches[0] || e.changedTouches[0];

            const rawX = touch.clientX - rect.left;
            const rawY = touch.clientY - rect.top;

            const canvasX = rawX / zoomLevel;
            const canvasY = rawY / zoomLevel;

            const labelPadding = parseInt(canvas.dataset.labelPadding || "0", 10);
            const adjustedY = canvasY - labelPadding;

            return { x: canvasX, y: adjustedY };
        },
        [renderedImageGeom, zoomLevel]
    );

    // Mobile-optimized resize handle detection with smaller threshold
    const getMobileResizeHandleAtPoint = useCallback(
        (point: Point, annotation: T): ResizeHandle | null => {
            const boundingBox = annotation as unknown as BoundingBox;
            const { x, y, width, height } = boundingBox;
            const tlx = x - width / 2;
            const tly = y - height / 2;
            const brx = x + width / 2;
            const bry = y + height / 2;
            const midX = x;
            const midY = y;

            // Smaller handle radius for mobile to reduce accidental resizing (8px instead of 15px)
            const mobileHandleRadius = 8;

            const handlesDef: { x: number; y: number; cursor: ResizeHandle }[] = [
                { x: tlx, y: tly, cursor: "nw" },
                { x: midX, y: tly, cursor: "n" },
                { x: brx, y: tly, cursor: "ne" },
                { x: tlx, y: midY, cursor: "w" },
                { x: brx, y: midY, cursor: "e" },
                { x: tlx, y: bry, cursor: "sw" },
                { x: midX, y: bry, cursor: "s" },
                { x: brx, y: bry, cursor: "se" }
            ];

            for (const handle of handlesDef) {
                if (
                    point.x >= handle.x - mobileHandleRadius &&
                    point.x <= handle.x + mobileHandleRadius &&
                    point.y >= handle.y - mobileHandleRadius &&
                    point.y <= handle.y + mobileHandleRadius
                ) {
                    return handle.cursor;
                }
            }
            return null;
        },
        []
    );

    // Use native event listeners to properly handle preventDefault with passive: false
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || isViewOnly) return;

        const handleNativeTouchStart = (e: TouchEvent) => {
            e.preventDefault();
            const point = getPointFromNativeTouchEvent(e);

            if (isPointOutsideBounds(point)) return;

            let touchedAnnotation: T | null = null;
            for (const anno of annotationsToDrawScaled as unknown as ReadonlyArray<T>) {
                if (strategy.isPointInAnnotation(point, anno)) {
                    touchedAnnotation = anno;
                    break;
                }
            }

            if (touchedAnnotation) {
                setSelectedAnnotation(touchedAnnotation);
                // Also update the context selection state so UI components respond
                setSelectedAnnotationId(touchedAnnotation.id);

                // Check if touch is on a resize handle with smaller threshold for mobile
                const resizeHandle = getMobileResizeHandleAtPoint(point, touchedAnnotation);

                if (resizeHandle) {
                    // For mobile, start with resize but with tighter constraints
                    setTouchState({
                        action: "resizing",
                        annotation: touchedAnnotation,
                        startPoint: point,
                        originalAnnotation: touchedAnnotation,
                        resizeHandle: resizeHandle
                    });
                } else {
                    // Start moving
                    setTouchState({
                        action: "moving",
                        annotation: touchedAnnotation,
                        startPoint: point,
                        originalAnnotation: touchedAnnotation,
                        resizeHandle: null
                    });
                }
            } else {
                // Touched empty space
                setSelectedAnnotation(null);
                // Also clear the context selection state
                setSelectedAnnotationId(null);
                setTouchState({
                    action: "idle",
                    annotation: null,
                    startPoint: null,
                    originalAnnotation: null,
                    resizeHandle: null
                });
                if (onCanvasClickWithoutAnnotation) {
                    onCanvasClickWithoutAnnotation();
                }
            }
        };

        const handleNativeTouchMove = (e: TouchEvent) => {
            if (touchState.action === "idle" || !touchState.startPoint || !touchState.annotation)
                return;
            e.preventDefault();

            const currentPoint = getPointFromNativeTouchEvent(e);

            if (isPointOutsideBounds(currentPoint)) return;

            const deltaX = currentPoint.x - touchState.startPoint.x;
            const deltaY = currentPoint.y - touchState.startPoint.y;

            let updatedAnnotation: T | null = null;

            if (touchState.action === "moving") {
                updatedAnnotation = moveAnnotation(touchState.originalAnnotation!, deltaX, deltaY);
            } else if (
                touchState.action === "resizing" &&
                touchState.originalAnnotation &&
                touchState.resizeHandle
            ) {
                updatedAnnotation = resizeAnnotation(
                    touchState.originalAnnotation,
                    touchState.resizeHandle,
                    deltaX,
                    deltaY,
                    touchState.originalAnnotation
                );
            }

            if (updatedAnnotation) {
                // Update the annotation in real-time (like desktop)
                setCurrentFrameAnnotations((prevFrameAnnotations: Annotation[]) =>
                    prevFrameAnnotations.map((a) =>
                        a.id === touchState.annotation!.id
                            ? (updatedAnnotation as unknown as Annotation)
                            : a
                    )
                );
                setSelectedAnnotation(updatedAnnotation);

                // Update touch state with the new annotation position
                setTouchState((prev) => ({ ...prev, annotation: updatedAnnotation }));
            }
        };

        const handleNativeTouchEnd = async (e: TouchEvent) => {
            if (touchState.action === "idle") return;
            e.preventDefault();

            // Generate thumbnail for the final position
            // Use renderedImageGeom (un-zoomed canvas dimensions) as the source,
            // not mediaRect which includes CSS transform zoom
            if (touchState.annotation && originalMediaDimensions && renderedImageGeom) {
                const currentAnnotation = touchState.annotation as unknown as Annotation;

                const { scaledRect: annotationForThumbnail } = scaleRect({
                    source: { width: renderedImageGeom.width, height: renderedImageGeom.height },
                    target: originalMediaDimensions,
                    rect: currentAnnotation
                });

                try {
                    const thumbnailUrl =
                        await generateThumbnailForCurrentFrame(annotationForThumbnail);
                    const finalAnnotation = {
                        ...currentAnnotation,
                        thumbnailDataUrl: thumbnailUrl ?? undefined
                    };

                    setCurrentFrameAnnotations((prevFrameAnnotations: Annotation[]) =>
                        prevFrameAnnotations.map((a) =>
                            a.id === touchState.annotation!.id ? finalAnnotation : a
                        )
                    );
                    setSelectedAnnotation(finalAnnotation as unknown as T);
                } catch (error) {
                    console.warn("Failed to generate thumbnail after touch end:", error);
                }
            }

            // Reset touch state
            setTouchState({
                action: "idle",
                annotation: null,
                startPoint: null,
                originalAnnotation: null,
                resizeHandle: null
            });
        };

        const handleNativeTouchCancel = (e: TouchEvent) => {
            e.preventDefault();
            // Reset touch state on cancel (when system interrupts the touch)
            setTouchState({
                action: "idle",
                annotation: null,
                startPoint: null,
                originalAnnotation: null,
                resizeHandle: null
            });
        };

        canvas.addEventListener("touchstart", handleNativeTouchStart, { passive: false });
        canvas.addEventListener("touchmove", handleNativeTouchMove, { passive: false });
        canvas.addEventListener("touchend", handleNativeTouchEnd, { passive: false });
        canvas.addEventListener("touchcancel", handleNativeTouchCancel, { passive: false });

        return () => {
            canvas.removeEventListener("touchstart", handleNativeTouchStart);
            canvas.removeEventListener("touchmove", handleNativeTouchMove);
            canvas.removeEventListener("touchend", handleNativeTouchEnd);
            canvas.removeEventListener("touchcancel", handleNativeTouchCancel);
        };
    }, [
        isViewOnly,
        touchState,
        strategy,
        annotationsToDrawScaled,
        setSelectedAnnotation,
        onCanvasClickWithoutAnnotation,
        moveAnnotation,
        resizeAnnotation,
        setCurrentFrameAnnotations,
        originalMediaDimensions,
        mediaRef,
        generateThumbnailForCurrentFrame,
        getPointFromNativeTouchEvent,
        setSelectedAnnotationId,
        getMobileResizeHandleAtPoint,
        renderedImageGeom,
        isPointOutsideBounds
    ]);

    return (
        <canvas
            ref={canvasRef}
            data-annotation-canvas
            className={twClassNames(className, "absolute focus:outline-none focus:ring-0")}
            style={{
                cursor: cursor,
                touchAction: isViewOnly || onlyShowPredictions ? "auto" : "none",
                // Apply same zoom transform as media element to keep canvas aligned
                transform: `translate(-50%, -50%) scale(${zoomLevel}) translate(${zoomX}px, ${zoomY}px)`,
                transformOrigin: "center center"
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            onDoubleClick={handleDoubleClick}
            onAuxClick={(e) => e.preventDefault()} // Prevent default middle-click behavior (auto-scroll)
            tabIndex={isViewOnly || onlyShowPredictions ? -1 : 0} // Make focusable only if not read-only
        />
    );
};
